import React, { useState } from 'react';
import { X, Copy, Eye, EyeOff } from 'lucide-react';
// Password generation moved to backend
import { Button } from '@atoms';
import { trpc } from '@lib/trpc';
import toast from 'react-hot-toast';

interface AdminResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  userName: string;
  userEmail: string;
}

export const AdminResetPasswordModal: React.FC<AdminResetPasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess: _onSuccess,
  userId,
  userName,
  userEmail,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [passwordToken, setPasswordToken] = useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [lastCopyAction, setLastCopyAction] = useState<string>('');

  // Get trpc utils to control caching
  const utils = trpc.useUtils();
  
  // tRPC mutations for password reset and retrieval
  const resetPasswordMutation = trpc.admin.resetPassword.useMutation({
    onMutate: async () => {
      // Cancel any outgoing refetches to prevent optimistic updates
      await utils.auth.validateSessionWithRoles.cancel();
    },
    onSuccess: (data) => {
      // Do absolutely nothing - no invalidations, no refetches
    },
    onError: (error) => {
      // Do nothing
    },
    onSettled: (data, error) => {
      // Do nothing - prevent any automatic refetches
    },
  });
  
  const retrievePasswordMutation = trpc.admin.retrievePassword.useMutation();

  const handleSubmit = async () => {
    if (isLoading) return; // Prevent duplicate submissions
    setIsLoading(true);
    
    try {
      // SECURITY: Backend-only password generation - no password transmitted from frontend
      const result = await resetPasswordMutation.mutateAsync({
        userId // No mode parameter needed - backend always generates securely
      });
      
      if (result.passwordToken) {
        // Store the secure token and retrieve the actual password
        setPasswordToken(result.passwordToken);
        await retrievePassword(result.passwordToken);
        toast.success('Password reset successfully', { id: 'admin-password-reset-success' });
      } else {
        throw new Error('Password reset completed but no password token received');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reset password', { id: 'password-reset-error' });
    } finally {
      setIsLoading(false);
    }
  };
  
  const retrievePassword = async (tokenId: string) => {
    try {
      const result = await retrievePasswordMutation.mutateAsync({ tokenId });
      if (result.success && result.password) {
        setTemporaryPassword(result.password);
      } else {
        throw new Error('Failed to retrieve password');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to retrieve password', { id: 'password-retrieve-error' });
      throw error;
    }
  };

  if (!isOpen) return null;

  // Show credentials screen after password generation
  if (temporaryPassword) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">✅ Password Reset Successfully</h2>
            
            <div className="bg-info-50 border border-info-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-info-800 mb-3">
                Please share these credentials with <strong>{userName}</strong>.
                They will be required to change the password on next login.
              </p>
            </div>

            {/* Email */}
            <div className="mb-4">
              <p className="text-xs text-gray-600 mb-1">Email</p>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-3">
                <p className="font-mono text-base text-gray-900">{userEmail}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Copy}
                  onClick={() => {
                    const actionKey = 'email-copy';
                    if (lastCopyAction === actionKey) return;
                    setLastCopyAction(actionKey);
                    navigator.clipboard.writeText(userEmail);
                    toast.success('Email copied!', { id: 'email-copied' });
                    setTimeout(() => setLastCopyAction(''), 1000);
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-6">
              <p className="text-xs text-gray-600 mb-1">Temporary Password</p>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-3">
                <p className="font-mono text-base text-gray-900 flex-1 mr-2">
                  {isPasswordVisible ? temporaryPassword : '••••••••••••••••'}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={isPasswordVisible ? EyeOff : Eye}
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    title={isPasswordVisible ? 'Hide password' : 'Show password'}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={Copy}
                    onClick={() => {
                      const actionKey = 'password-copy';
                      if (lastCopyAction === actionKey) return;
                      setLastCopyAction(actionKey);
                      navigator.clipboard.writeText(temporaryPassword);
                      toast.success('Password copied!', { id: 'password-copied' });
                      setTimeout(() => setLastCopyAction(''), 1000);
                    }}
                    title="Copy password"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  const actionKey = 'credentials-copy';
                  if (lastCopyAction === actionKey) return;
                  setLastCopyAction(actionKey);
                  const credentials = `Email: ${userEmail}\nPassword: ${temporaryPassword}`;
                  navigator.clipboard.writeText(credentials);
                  toast.success('All credentials copied!', { id: 'credentials-copied' });
                  setTimeout(() => setLastCopyAction(''), 1000);
                }}
                icon={Copy}
                className="flex-1"
              >
                Copy Both
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  // Clean up all state and close modal
                  setTemporaryPassword(null);
                  setPasswordToken(null);
                  setIsPasswordVisible(false);
                  setLastCopyAction('');
                  onClose();
                }}
                className="flex-1"
              >
                Done
              </Button>
            </div>

            <div className="mt-4 p-3 bg-warning-50 rounded-lg">
              <p className="text-xs text-warning-700 text-center">
                ⚠️ The user must change this password on their next login
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
            <p className="text-sm text-gray-600 mt-1">
              Reset password for user: <span className="font-medium">{userName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Warning:</strong> This will reset the user's password and invalidate all their current sessions.
              A secure password will be generated automatically on the backend. The user will need to log in again with the new password.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outlined"
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              variant="primary"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
